import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import type {
  ReportCell,
  ReportCellKind,
  ReportColumn,
  ReportResult,
  ReportRow,
} from '@servwell/shared';
import { CSV_FORMULA_START } from '@servwell/shared';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { KpiGrid, KpiCard } from '../ui/Page';
import { EmptyState } from '../ui/EmptyState';
import { Notice } from '../ui/Badge';
import { money } from '../money';
import { IconReports } from '../icons';

/**
 * ONE renderer for all fifteen reports (PRD v2 §7 V4). Every report speaks the same
 * `ReportResult` shape, so they all get the same sorting, the same search, the same CSV,
 * the same totals row and — the part that matters — the same em-dash for a value that
 * isn't there. Fifteen bespoke pages would have drifted on all five within a month.
 *
 * This is a renderer, not a builder: the shop picks a report from a closed server-side
 * catalogue, it does not assemble one. The matrix skips report builders on purpose.
 */

const RIGHT: ReportCellKind[] = ['money', 'number', 'percent', 'duration'];

function formatCell(value: ReportCell | undefined, kind: ReportCellKind): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (kind) {
    case 'money':
      return money(Number(value));
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'number':
      return Number(value).toLocaleString('en-US');
    case 'duration':
      return `${Number(value).toFixed(1)}h`;
    case 'date':
      return new Date(`${String(value)}T12:00:00`).toLocaleDateString();
    default:
      return String(value);
  }
}

/** CSV of exactly what's on screen — same columns, same order, unformatted numbers. */
export function toCsv(result: ReportResult): string {
  const esc = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const head = result.columns.map((c) => esc(c.label)).join(',');
  const body = result.rows.map((r) =>
    result.columns
      .map((c) => {
        const v = r[c.key];
        if (v === null || v === undefined) return '';
        // Money leaves as dollars, not cents — a bookkeeper opening this in a spreadsheet
        // should not have to remember our storage unit.
        if (c.kind === 'money') return String(Number(v) / 100);
        const s = String(v);
        // Words only: a number, a percent or a duration below zero is a number, and keeps
        // its sign as it is.
        const words = c.kind === 'text' || c.kind === 'link';
        return esc(words && CSV_FORMULA_START.test(s) ? `'${s}` : s);
      })
      .join(','),
  );
  return [head, ...body].join('\n');
}

export function downloadReportCsv(result: ReportResult, title: string): void {
  // The byte-order mark tells Excel the file is UTF-8, or "Aug 1–10" opens as "Aug 1â€“10".
  const blob = new Blob(['\uFEFF', toCsv(result)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function LinkCell({ column, row }: { column: ReportColumn; row: ReportRow }) {
  const label = String(row[column.key] ?? '—');
  const id = column.idKey ? row[column.idKey] : null;
  if (!column.href || !id) return <>{label}</>;
  return (
    <Link
      to={column.href.replace(':id', String(id))}
      className="font-medium text-[var(--sw-brand-text)] hover:underline"
    >
      {label}
    </Link>
  );
}

export function ReportView({
  result,
  search,
  emptyTitle,
  tableId,
}: {
  result: ReportResult;
  search: string;
  emptyTitle: string;
  /** Per REPORT, not one id for all fifteen — hiding "Labour" on job-profitability must
   *  not hide a same-keyed column on a different report. */
  tableId: string;
}) {
  const columns = useMemo<DataTableColumn<ReportRow>[]>(() => {
    // Totals are FOOTER CELLS on the data table itself now. They used to be a separate
    // `<table min-w-[420px]>` below it — two tables sizing and scrolling independently,
    // so at 390px a total sat under a column it did not total. They also summed the
    // UNFILTERED rows while a search was active; the footer receives the filtered set,
    // so the total now describes the rows on screen.
    const hasTotals = result.columns.some((c) => c.total);
    return result.columns.map((c, i) => ({
      key: c.key,
      label: c.label,
      align: RIGHT.includes(c.kind) ? ('right' as const) : ('left' as const),
      // Sort on the RAW value so money sorts by cents and dates by string order —
      // sorting the formatted text would put $9.00 after $10.00.
      value: (r: ReportRow) => r[c.key],
      // …but search the FORMATTED text, so typing "1,200" finds what the eye can see.
      searchValue: (r: ReportRow) =>
        c.kind === 'link' ? String(r[c.key] ?? '') : formatCell(r[c.key], c.kind),
      cell: (r: ReportRow) => {
        if (c.kind === 'link') return <LinkCell column={c} row={r} />;
        // formatCell already returns the em-dash for a missing value — `dash` would
        // double-apply it, and two implementations of one law is one too many.
        return (
          <span className={RIGHT.includes(c.kind) ? 'tabular-nums' : undefined}>
            {formatCell(r[c.key], c.kind)}
          </span>
        );
      },
      // The `Total (N rows)` LABEL is deliberately not here — bound to a column it
      // vanishes when the user hides that column (Codex 103 C3); it rides the
      // `footerLabel` prop instead and follows the first visible column.
      footer:
        hasTotals && c.total
          ? (rows: ReportRow[]) => {
              const vals = rows
                .map((r) => r[c.key])
                .filter((v): v is number => typeof v === 'number');
              return formatCell(
                vals.length > 0 ? vals.reduce((s, v) => s + v, 0) : null,
                c.kind,
              );
            }
          : undefined,
    }));
  }, [result.columns]);

  // Report rows have no id of their own — a month, a payment method, a person's name. The
  // position in the result IS the identity, so it gets attached once here rather than
  // asking fifteen server-side reports to invent a primary key.
  const keyed = useMemo(
    () => result.rows.map((r, i) => ({ ...r, __row: i })),
    [result.rows],
  );

  return (
    <div className="space-y-3">
      {result.figures.length > 0 ? (
        <KpiGrid>
          {result.figures.slice(0, 4).map((f) => (
            <KpiCard
              key={f.label}
              label={f.label}
              value={
                f.value === null || f.value === undefined
                  ? '—'
                  : formatCell(
                      typeof f.value === 'number' ? f.value : String(f.value),
                      f.kind,
                    )
              }
              tone={f.tone}
            />
          ))}
        </KpiGrid>
      ) : null}

      <DataTable
        tableId={tableId}
        columns={columns}
        rows={keyed}
        rowKey={(r) => String(r.__row)}
        search={search}
        footerLabel={(rows) =>
          `Total (${rows.length} row${rows.length === 1 ? '' : 's'})`
        }
        empty={
          <EmptyState
            icon={IconReports}
            title={emptyTitle}
            description="Nothing in this period. Widen the dates, or come back once there's more work behind you."
          />
        }
      />

      {/* What the report can't tell you, next to what it can. A limitation in a footnote
          nobody reads is how a shop makes a decision on a number that never meant that. */}
      {result.caveats.length > 0 ? (
        <Notice tone="info">
          <ul className="space-y-1">
            {result.caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </div>
  );
}
